import { useGoogleAccessToken } from '@/composables/useGoogleAccessToken'
import { googleFetch, toSearchParams } from '@/services/googleRestClient'
import { convertToGoogleError } from '@/utils/googleError'
import { logger } from '@/utils/logger'
import type { ActorId } from '@/types/google'

// How to add new Google APIs. Example: Google People API
// 1. add `@types/gapi.client.<api>-v<version>` to devDependencies in package.json, run `pnpm install`
// 2. add `/// <reference types="gapi.client.<api>-v<version>" />` to env.d.ts
// 3. add a function below that calls googleFetch() against the API's REST endpoint

export interface DriveActivityQueryOptions {
    // TODO: enable 'PERMISSION_CHANGE' actions later
    actionFilter?: string // space-separated action_detail_case values, e.g. 'COMMENT'
    mode?: 'item' | 'ancestor' // 'item': query activity for gid itself. 'ancestor': query activity for gid's subtree
}

export async function driveActivityQuery (gid: string, fromTs: number, toTs: number, pageToken?: string, options?: DriveActivityQueryOptions):
Promise<gapi.client.driveactivity.QueryDriveActivityResponse> {
    const actionFilter = `detail.action_detail_case:(${options?.actionFilter ?? 'CREATE EDIT RENAME MOVE DELETE COMMENT RESTORE'})`
    const mode = options?.mode ?? 'ancestor'

    try {
        // a strict check "time > ${fromTs}" guarantees no duplicate actions are fetched
        const resource = {
            ...(mode === 'item' ? { itemName: `items/${gid}` } : { ancestorName: `items/${gid}` }),
            pageSize: 20,
            filter: `time > ${fromTs} AND time <= ${toTs} AND ${actionFilter}`, // milliseconds since Jan 1, 1970 or in RFC 3339 format.
            ...(pageToken && { pageToken }),
        }

        const searchParams = toSearchParams({ fields: '*' }) // 'nextPageToken,activities(target,actions(detail,time))'

        return await googleFetch<gapi.client.driveactivity.QueryDriveActivityResponse>(
            `https://driveactivity.googleapis.com/v2/activity:query?${searchParams}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(resource),
            },
        )
    } catch (error) {
        logger.error('Error calling Drive Activity API:', error)
        throw convertToGoogleError(error)
    }
}

export async function useValidAccessToken () {
    const { accessTokenFn } = useGoogleAccessToken()
    const accessToken = await accessTokenFn()
    if (!accessToken) {
        throw new Error('No access token available')
    }

    return accessToken
}

export async function peopleGetBatchGet (ids: ActorId[]): Promise<gapi.client.people.GetPeopleResponse> {
    try {
        // Convert ids to resource names format (people/{id})
        const resourceNames = ids.map(id => `people/${id}`)

        const searchParams = toSearchParams({
            resourceNames,
            personFields: 'names,emailAddresses,photos',
            sources: ['READ_SOURCE_TYPE_PROFILE', 'READ_SOURCE_TYPE_CONTACT', 'READ_SOURCE_TYPE_DOMAIN_CONTACT', 'READ_SOURCE_TYPE_OTHER_CONTACT'],
        })

        return await googleFetch<gapi.client.people.GetPeopleResponse>(
            `https://people.googleapis.com/v1/people:batchGet?${searchParams}`,
        )
    } catch (error) {
        logger.error('Error calling People API:', error)
        throw convertToGoogleError(error)
    }
}

export async function driveFilesGet (gid: string, fields = '*'): Promise<gapi.client.drive.File> {
    try {
        // specify fields to reduce response size, e.g. 'id,name,mimeType,parents,createdTime,hasThumbnail,starred,modifiedTime,viewedByMeTime,sharedWithMeTime,shared,imageMediaMetadata,videoMediaMetadata,size,thumbnailLink,webContentLink,webViewLink'
        const searchParams = toSearchParams({ fields, supportsAllDrives: true })

        return await googleFetch<gapi.client.drive.File>(
            `https://www.googleapis.com/drive/v3/files/${gid}?${searchParams}`,
        )
    } catch (error) {
        logger.error('Error calling Drive API:', error)
        throw convertToGoogleError(error)
    }
}

/**
 * Drive `files.get` for many files in a single HTTP request (Drive multipart batch endpoint).
 * Files that can't be fetched (e.g. no access) are omitted from the result.
 */
export async function driveFilesGetBatch (gids: string[], fields = '*'): Promise<gapi.client.drive.File[]> {
    if (gids.length === 0) {
        return []
    }

    try {
        const boundary = `batch_${Math.random().toString(36).slice(2)}`
        const searchParams = toSearchParams({ fields, supportsAllDrives: true })

        const body = gids.map((gid, index) =>
            `--${boundary}\r\nContent-Type: application/http\r\nContent-ID: <item-${index}>\r\n\r\n`
            + `GET /drive/v3/files/${encodeURIComponent(gid)}?${searchParams}\r\n`,
        ).join('') + `--${boundary}--`

        const responseText = await googleFetch<string>('https://www.googleapis.com/batch/drive/v3', {
            method: 'POST',
            headers: { 'Content-Type': `multipart/mixed; boundary=${boundary}` },
            body,
        }, true)

        // Each part is "<MIME headers>\r\n\r\nHTTP/1.1 200 OK\r\n<HTTP headers>\r\n\r\n<JSON body>"
        const files: gapi.client.drive.File[] = []
        const responseBoundary = /^--(\S+)/.exec(responseText.trimStart())?.[1]
        if (!responseBoundary) {
            throw new Error('Invalid Drive batch response')
        }

        for (const part of responseText.split(`--${responseBoundary}`)) {
            const [, httpResponse] = part.split(/\r?\n\r?\n(?=HTTP\/)/)
            if (!httpResponse || !/^HTTP\/\S+ 2\d\d/.test(httpResponse)) {
                continue
            }

            const json = httpResponse.slice(httpResponse.search(/\{/), httpResponse.lastIndexOf('}') + 1)
            files.push(JSON.parse(json) as gapi.client.drive.File)
        }

        return files
    } catch (error) {
        logger.error('Error calling Drive API batch:', error)
        throw convertToGoogleError(error)
    }
}

export async function driveDrivesGet (driveId: string, fields = '*'): Promise<gapi.client.drive.Drive> {
    try {
        const searchParams = toSearchParams({ fields })

        return await googleFetch<gapi.client.drive.Drive>(
            `https://www.googleapis.com/drive/v3/drives/${driveId}?${searchParams}`,
        )
    } catch (error) {
        logger.error('Error calling Drive API:', error)
        throw convertToGoogleError(error)
    }
}

export async function driveCommentsList (
    fileId: string,
    startModifiedTime: string,
    excludeReplies = true,
): Promise<gapi.client.drive.Comment[]> {
    try {
        const fields = excludeReplies ? 'nextPageToken,comments(id,createdTime,modifiedTime)' : '*'

        const comments: gapi.client.drive.Comment[] = []
        let pageToken: string | undefined

        do {
            const searchParams = toSearchParams({ startModifiedTime, fields, ...(pageToken && { pageToken }) })

            const response = await googleFetch<gapi.client.drive.CommentList>(
                `https://www.googleapis.com/drive/v3/files/${fileId}/comments?${searchParams}`,
            )

            comments.push(...(response.comments ?? []))
            pageToken = response.nextPageToken
        } while (pageToken)

        return comments
    } catch (error) {
        logger.error('Error calling Drive API:', error)
        throw convertToGoogleError(error)
    }
}

export async function driveCommentsGet (
    fileId: string,
    commentId: string,
): Promise<gapi.client.drive.Comment> {
    try {
        // request all fields, we need 'replies[]' which is excluded by default
        const searchParams = toSearchParams({ fields: '*' })

        return await googleFetch<gapi.client.drive.Comment>(
            `https://www.googleapis.com/drive/v3/files/${fileId}/comments/${commentId}?${searchParams}`,
        )
    } catch (error) {
        logger.error('Error calling Drive API:', error)
        throw convertToGoogleError(error)
    }
}
